import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { accessi, persone } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LogIn, LogOut, Search, Camera, X, Check, AlertCircle, Users, Clock } from "lucide-react";
import { toast } from "sonner";
import { Html5Qrcode } from "html5-qrcode";
import { useEffect, useRef } from "react";

const AREE_CAMPO = [
  { value: "ingresso_principale", label: "Ingresso Principale" },
  { value: "ingresso_secondario", label: "Ingresso Secondario" },
  { value: "ingresso_mezzi", label: "Ingresso Mezzi" },
];

export default function ControlloAccessi() {
  const [tipoAccesso, setTipoAccesso] = useState("entrata");
  const [areaAccesso, setAreaAccesso] = useState("ingresso_principale");
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [search, setSearch] = useState("");
  const scannerRef = useRef(null);
  const html5QrcodeScannerRef = useRef(null);

  const queryClient = useQueryClient();

  const { data: accessiData, isLoading } = useQuery({
    queryKey: ['accessi'],
    queryFn: () => accessi.list({ limit: 100 }),
    refetchInterval: 10000,
  });

  const accessoMutation = useMutation({
    mutationFn: async (qrCode) => {
      const persona = await persone.getByQr(qrCode);
      return accessi.create({
        persona_id: persona.id,
        tipo_accesso: tipoAccesso,
        area_accesso: areaAccesso,
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['accessi']);
      setScanResult({ success: true, data, tipo: tipoAccesso });
      toast.success(`${tipoAccesso === 'entrata' ? 'Entrata' : 'Uscita'} registrata!`);
    },
    onError: (error) => {
      setScanResult({ success: false, error: error.message });
      toast.error(error.message);
    },
  });

  useEffect(() => {
    if (scanning && scannerRef.current) {
      const scanner = new Html5Qrcode("access-qr-reader");
      html5QrcodeScannerRef.current = scanner;
      
      scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          accessoMutation.mutate(decodedText);
          stopScanning();
        },
        () => {}
      ).catch((err) => {
        console.error("Camera error:", err);
      });
    }

    return () => {
      if (html5QrcodeScannerRef.current && html5QrcodeScannerRef.current.isScanning) {
        html5QrcodeScannerRef.current.stop().catch(() => {});
      }
    };
  }, [scanning]);

  const stopScanning = () => {
    if (html5QrcodeScannerRef.current && html5QrcodeScannerRef.current.isScanning) {
      html5QrcodeScannerRef.current.stop().catch(() => {});
    }
    setScanning(false);
  };

  // Calculate stats
  const oggi = new Date().toISOString().split('T')[0];
  const accessiOggi = accessiData?.filter(a => a.timestamp_accesso?.startsWith(oggi)) || [];
  const entrateOggi = accessiOggi.filter(a => a.tipo_accesso === 'entrata').length;
  const usciteOggi = accessiOggi.filter(a => a.tipo_accesso === 'uscita').length;
  const presentiOra = Math.max(0, entrateOggi - usciteOggi);

  const filteredAccessi = accessiData?.filter(a => {
    if (!search) return true;
    const personaNome = `${a.persona?.nome || ''} ${a.persona?.cognome || ''}`.toLowerCase();
    return personaNome.includes(search.toLowerCase());
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="controllo-accessi-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Controllo Accessi</h1>
        <p className="text-slate-500 mt-1">Registrazione entrate e uscite dal campo</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-50 rounded-lg">
                <LogIn className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{entrateOggi}</p>
                <p className="text-xs text-slate-500">Entrate Oggi</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-50 rounded-lg">
                <LogOut className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">{usciteOggi}</p>
                <p className="text-xs text-slate-500">Uscite Oggi</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">{presentiOra}</p>
                <p className="text-xs text-slate-500">Presenti Ora</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Scanner Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Scanner Controls */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Registra Accesso</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Type Selection */}
            <div className="flex gap-2">
              <Button
                variant={tipoAccesso === 'entrata' ? 'default' : 'outline'}
                className={tipoAccesso === 'entrata' ? 'bg-green-600 hover:bg-green-700 flex-1' : 'flex-1'}
                onClick={() => setTipoAccesso('entrata')}
                data-testid="btn-entrata"
              >
                <LogIn className="h-4 w-4 mr-2" />
                Entrata
              </Button>
              <Button
                variant={tipoAccesso === 'uscita' ? 'default' : 'outline'}
                className={tipoAccesso === 'uscita' ? 'bg-red-600 hover:bg-red-700 flex-1' : 'flex-1'}
                onClick={() => setTipoAccesso('uscita')}
                data-testid="btn-uscita"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Uscita
              </Button>
            </div>

            {/* Area Selection */}
            <div>
              <label className="text-sm font-medium mb-2 block">Punto di Accesso</label>
              <Select value={areaAccesso} onValueChange={setAreaAccesso}>
                <SelectTrigger data-testid="select-area-accesso">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AREE_CAMPO.map(area => (
                    <SelectItem key={area.value} value={area.value}>{area.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Scanner */}
            {!scanning ? (
              <Button 
                onClick={() => { setScanResult(null); setScanning(true); }}
                size="lg"
                className={`w-full ${tipoAccesso === 'entrata' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
                data-testid="start-access-scan-btn"
              >
                <Camera className="h-5 w-5 mr-2" />
                Scansiona Badge {tipoAccesso === 'entrata' ? 'Entrata' : 'Uscita'}
              </Button>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Scansione in corso...</span>
                  <Button variant="ghost" size="icon" onClick={stopScanning}>
                    <X className="h-5 w-5" />
                  </Button>
                </div>
                <div ref={scannerRef} id="access-qr-reader" className="qr-scanner-container" />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Access */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Ultimi Accessi
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Cerca persona..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {filteredAccessi?.slice(0, 20).map(accesso => (
                <div 
                  key={accesso.id}
                  className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg"
                >
                  <div className={`p-2 rounded-lg ${accesso.tipo_accesso === 'entrata' ? 'bg-green-100' : 'bg-red-100'}`}>
                    {accesso.tipo_accesso === 'entrata' ? (
                      <LogIn className="h-4 w-4 text-green-600" />
                    ) : (
                      <LogOut className="h-4 w-4 text-red-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">
                      {accesso.persona?.nome} {accesso.persona?.cognome}
                    </p>
                    <p className="text-xs text-slate-500">{accesso.area_accesso?.replace("_", " ")}</p>
                  </div>
                  <div className="text-right">
                    <Badge variant={accesso.tipo_accesso === 'entrata' ? 'default' : 'destructive'} className="text-xs">
                      {accesso.tipo_accesso}
                    </Badge>
                    <p className="text-xs text-slate-400 mt-1">
                      {new Date(accesso.timestamp_accesso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
              {(!filteredAccessi || filteredAccessi.length === 0) && (
                <p className="text-center text-slate-500 py-8">Nessun accesso registrato</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Result Dialog */}
      <Dialog open={scanResult !== null} onOpenChange={() => setScanResult(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {scanResult?.success ? "Accesso Registrato" : "Errore"}
            </DialogTitle>
          </DialogHeader>
          <div className="text-center py-4">
            {scanResult?.success ? (
              <>
                <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 ${
                  scanResult.tipo === 'entrata' ? 'bg-green-100' : 'bg-red-100'
                }`}>
                  {scanResult.tipo === 'entrata' ? (
                    <LogIn className="h-10 w-10 text-green-600" />
                  ) : (
                    <LogOut className="h-10 w-10 text-red-600" />
                  )}
                </div>
                <p className={`text-lg font-semibold ${scanResult.tipo === 'entrata' ? 'text-green-600' : 'text-red-600'}`}>
                  {scanResult.tipo === 'entrata' ? 'Entrata Registrata' : 'Uscita Registrata'}
                </p>
              </>
            ) : (
              <>
                <div className="w-20 h-20 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-4">
                  <AlertCircle className="h-10 w-10 text-red-600" />
                </div>
                <p className="text-lg font-semibold text-red-600">Errore</p>
                <p className="text-slate-600 mt-2">{scanResult?.error}</p>
              </>
            )}
          </div>
          <Button onClick={() => setScanResult(null)} className="w-full">
            Chiudi
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
